/**
 * The navigation drawer's eighteen items, as data (Phase 1E).
 *
 * Every string is copied from `components/menu/MenuPanel.vue`'s template and
 * its `menuItems` search index. The Playwright suite asserts all the slugs, so
 * they may not drift.
 *
 * `purchase-method` is the exception, added in Phase 1I: the Purchase Method
 * Analysis modal was reachable only from the stat cards' action row, which 1H
 * deleted, leaving a modal the app had no way to open. The menu is where the
 * rest of the analyses live, so it goes there rather than back onto a card —
 * it has no single number to put on one.
 *
 * Five of the six `tour` values are load-bearing: `tourSteps.js` anchors a step
 * on each of data-collection, import-mock-data, optimization-config,
 * simulation-config and export-reports, and `MenuPanel.test.tsx` derives that
 * list from the tour rather than repeating it. `guided-tour` is the odd one:
 * it marks the item that STARTS a tour, and no step points at it.
 */

export type MenuSection = { name: string; step: string | null }

export const MENU_SECTIONS: MenuSection[] = [
  { name: 'Data', step: '1' },
  { name: 'Design', step: '2' },
  { name: 'Analyze', step: '3' },
  { name: 'Report', step: '4' },
  // The only section without a step number.
  { name: 'View & Tools', step: null },
]

/** `killer` and `compact` render identically; they differ in hover colour and in the text's classes. */
export type Emphasis = 'killer' | 'compact' | 'special'

export type MenuItem = {
  slug: string
  section: string
  /** The guided tour's anchor, where it has one. */
  tour: string | null
  icon: string
  title: string
  subtitle: string | null
  emphasis: Emphasis
  /** What the search also matches on. Lower-case by convention — see `matchesSearch`. */
  aliases: string
  /** A switch row has no click of its own; the control carries the action. */
  kind: 'action' | 'switch'
  /**
   * The feature this item takes you into, for the "you are here" highlight.
   *
   * Shared where the Vue drawer shared it: Import Data and Load Mock Network
   * are both `data-management`, PowerPoint and Excel are both `reports` — open
   * either and both rows light up, because they are two doors into one place.
   * Items without one never highlighted: the tour, the two switches, and
   * Network & Config, whose binding existed but whose setter was never called.
   */
  feature?: string
}

export const MENU_ITEMS: MenuItem[] = [
  {
    slug: 'import-data',
    section: 'Data',
    tour: 'data-collection',
    icon: 'mdi-database',
    title: 'Import Data',
    subtitle: 'import / export / delete data',
    emphasis: 'killer',
    aliases: 'data collection import export delete upload excel',
    kind: 'action',
    feature: 'data-management',
  },
  {
    slug: 'load-mock-network',
    section: 'Data',
    tour: 'import-mock-data',
    icon: 'mdi-flask-outline',
    title: 'Load Mock Network',
    subtitle: 'get comfortable using the app',
    emphasis: 'compact',
    aliases: 'import mock data sample demo',
    kind: 'action',
    feature: 'data-management',
  },
  {
    slug: 'clean-data',
    section: 'Data',
    tour: null,
    icon: 'mdi-broom',
    title: 'Clean Data',
    subtitle: 'quality check & clean data',
    emphasis: 'compact',
    aliases: 'data cleaning quality validate',
    kind: 'action',
    feature: 'data-cleaning',
  },
  {
    slug: 'optimize-network',
    section: 'Design',
    tour: 'optimization-config',
    icon: 'mdi-hammer-wrench',
    title: 'Optimize Network',
    subtitle: 'Model facilities before investing millions',
    emphasis: 'killer',
    aliases: 'test new locations optimization greenfield solve run',
    kind: 'action',
    feature: 'optimization',
  },
  {
    slug: 'optimize-across-futures',
    section: 'Design',
    tour: null,
    icon: 'mdi-source-branch',
    title: 'Optimize Across Futures',
    subtitle: 'One network that works across many demands',
    emphasis: 'compact',
    aliases:
      'uncertainty stochastic risk robust two-stage scenarios vss value of stochastic solution cost of averaging design under uncertainty demand variability',
    kind: 'action',
    feature: 'uncertainty',
  },
  {
    slug: 'network-config',
    section: 'Design',
    tour: null,
    icon: 'mdi-tune',
    title: 'Network & Config',
    subtitle: 'Greenfield matching, lanes, facilities',
    emphasis: 'compact',
    aliases: 'configuration lanes facilities matching',
    kind: 'action',
  },
  {
    slug: 'add-node',
    section: 'Design',
    tour: null,
    icon: 'mdi-map-marker-plus',
    title: 'Add Node',
    subtitle: 'Add DCs, plants, or customers to network',
    emphasis: 'compact',
    aliases: 'facility dc plant customer new',
    kind: 'action',
    feature: 'add-node',
  },
  {
    slug: 'compare-scenarios',
    section: 'Analyze',
    tour: null,
    icon: 'mdi-lightbulb-on',
    title: 'Compare Scenarios',
    subtitle: 'baseline, prebuilt, & custom scenarios',
    emphasis: 'killer',
    aliases: 'run 50 scenarios baseline prebuilt custom',
    kind: 'action',
    feature: 'scenarios',
  },
  {
    slug: 'run-simulation',
    section: 'Analyze',
    tour: 'simulation-config',
    icon: 'mdi-flask',
    title: 'Run Simulation',
    subtitle: 'Risk & uncertainty',
    emphasis: 'compact',
    aliases: 'simulation monte carlo risk uncertainty',
    kind: 'action',
    feature: 'simulation-config',
  },
  {
    slug: 'purchase-method',
    section: 'Analyze',
    tour: null,
    icon: 'mdi-truck-check',
    title: 'Purchase Method',
    subtitle: 'Delivered vs. collected, by product and lane',
    emphasis: 'compact',
    aliases: 'purchase method delivered collected pickup freight terms incoterm sku',
    kind: 'action',
    feature: 'purchase-method',
  },
  {
    slug: 'negotiation',
    section: 'Analyze',
    tour: null,
    icon: 'mdi-handshake-outline',
    title: 'Negotiation',
    subtitle: 'What to renegotiate, and what you cannot win',
    emphasis: 'compact',
    aliases: 'renegotiate rates freight cost',
    kind: 'action',
    feature: 'negotiation',
  },
  {
    slug: 'powerpoint',
    section: 'Report',
    tour: 'export-reports',
    icon: 'mdi-file-powerpoint',
    title: 'PowerPoint',
    subtitle: 'Client-ready deck in 30 seconds',
    emphasis: 'killer',
    aliases: 'generate reports export ppt deck presentation',
    kind: 'action',
    feature: 'reports',
  },
  {
    slug: 'excel',
    section: 'Report',
    tour: null,
    icon: 'mdi-file-excel',
    title: 'Excel',
    subtitle: 'Lane, facility, and scenario workbooks',
    emphasis: 'compact',
    aliases: 'export reports xlsx workbook',
    kind: 'action',
    feature: 'reports',
  },
  {
    slug: 'presentation-mode',
    section: 'Report',
    tour: null,
    icon: 'mdi-presentation-play',
    title: 'Presentation Mode',
    subtitle: 'Switch to client-ready presentation views',
    emphasis: 'compact',
    aliases: 'client ready present',
    kind: 'action',
    feature: 'presentation-mode',
  },
  {
    slug: 'customize-map',
    section: 'View & Tools',
    tour: null,
    icon: 'mdi-palette',
    title: 'Customize Map',
    subtitle: 'Change icons, colors, and display options',
    emphasis: 'compact',
    aliases: 'icons colors display customize',
    kind: 'action',
    feature: 'customize-map',
  },
  {
    slug: 'filter-bar',
    section: 'View & Tools',
    tour: null,
    icon: 'mdi-magnify',
    title: 'Filter Bar',
    subtitle: 'Show/hide active nodes and lanes',
    emphasis: 'compact',
    aliases: 'filter show hide nodes lanes',
    kind: 'switch',
  },
  {
    slug: 'ai-chat',
    section: 'View & Tools',
    tour: null,
    icon: 'mdi-robot-outline',
    title: 'AI Chat',
    subtitle: null,
    emphasis: 'special',
    aliases: 'assistant advisor ask',
    kind: 'switch',
  },
  {
    slug: 'guided-tour',
    section: 'View & Tools',
    tour: 'guided-tour',
    icon: 'mdi-compass-outline',
    title: 'Guided Tour',
    subtitle: 'Walk through how it works, step by step',
    emphasis: 'compact',
    aliases: 'tour walkthrough help onboarding',
    kind: 'action',
  },
]

/**
 * The Vue version's predicate, asymmetry included: the query and the title and
 * section are lower-cased, the alias string is not. Every shipped alias is
 * already lower-case, so this is invisible today — and changing it would change
 * which items a query finds, which the suite asserts.
 */
export function matchesSearch(item: MenuItem, query: string): boolean {
  if (!query) return true
  const q = query.toLowerCase()
  return (
    item.title.toLowerCase().includes(q) ||
    item.section.toLowerCase().includes(q) ||
    (item.aliases || '').includes(q)
  )
}

/**
 * Whether anything at all matches. The section headers are gated on this rather
 * than on their own section's items, so every header stays visible while any
 * item anywhere matches — the Vue version's behaviour.
 */
export function anyMatches(query: string): boolean {
  return !query || MENU_ITEMS.some((item) => matchesSearch(item, query))
}

/** The items of one section, in order, that survive the current query. */
export function visibleItems(section: string, query: string): MenuItem[] {
  return MENU_ITEMS.filter((item) => item.section === section && matchesSearch(item, query))
}
